import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Trash2 } from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const AdminGodparents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const adminId = user?.id;
  const [godparents, setGodparents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ type: 'padrinho', status: 'solteiro' });
  const [createdInvite, setCreatedInvite] = useState(null);
  
  useEffect(() => {
    if (adminId) {
      fetchData();
    }
  }, [adminId]);
  
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [godparentsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/godparent/admin/list?admin_id=${adminId}`, { headers }),
        axios.get(`${API}/godparent/admin/invites?admin_id=${adminId}`, { headers })
      ]);
      
      setGodparents(godparentsRes.data.godparents || []);
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados de padrinhos');
    } finally {
      setLoading(false);
    }
  };
  
  const createInvite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/godparent/admin/create-invite?admin_id=${adminId}`,
        {
          godparent_type: newInvite.type,
          godparent_status: newInvite.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCreatedInvite(response.data);
      toast.success('Convite criado com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao criar convite:', error);
      toast.error('Erro ao criar convite');
    }
  };
  
  const copyLink = (url) => {
    // Se a URL já é completa (começa com http), usar diretamente
    // Senão, adicionar o domínio atual (funciona em produção e preview)
    let fullUrl = url;
    if (!url.startsWith('http')) {
      fullUrl = `${BASE_URL}${url}`;
    }
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiado!');
  };

  const deleteInvite = async (inviteId) => {
    if (!window.confirm('Tem certeza que deseja excluir este convite?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/godparent/admin/invite/${inviteId}?admin_id=${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Convite excluído!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir convite:', error);
      toast.error('Erro ao excluir convite');
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C79F5E] border-t-transparent"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="border-[#C79F5E]/30 text-[#C79F5E] hover:bg-[#C79F5E]/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-[#C79F5E]">🔥 Padrinhos & Madrinhas Hot</h2>
            <p className="text-gray-400 text-sm">Gerencie afiliados especiais que recebem 5% das comissões</p>
          </div>
        </div>
        <Button
          onClick={() => {
            setCreatedInvite(null);
            setShowCreateModal(true);
          }}
          className="bg-gradient-to-r from-[#C79F5E] to-[#8B6914] text-black font-semibold"
        >
          + Criar Convite
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{godparents.length}</p>
            <p className="text-sm text-gray-400">Padrinhos/Madrinhas</p>
          </CardContent>
        </Card>
        <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">
              {godparents.reduce((acc, g) => acc + (g.affiliates_count || 0), 0)}
            </p>
            <p className="text-sm text-gray-400">Total Afiliados</p>
          </CardContent>
        </Card>
        <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#C79F5E]">
              {formatCurrency(godparents.reduce((acc, g) => acc + (g.godparent_balance || 0), 0))}
            </p>
            <p className="text-sm text-gray-400">Comissões Totais</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Lista de Padrinhos/Madrinhas */}
      <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
        <CardHeader>
          <CardTitle className="text-[#C79F5E]">Padrinhos/Madrinhas Ativos</CardTitle>
        </CardHeader>
        <CardContent>
          {godparents.length === 0 ? (
            <p className="text-gray-400 text-center py-4">Nenhum padrinho/madrinha cadastrado</p>
          ) : (
            <div className="space-y-3">
              {godparents.map((gp) => (
                <div key={gp.id} className="flex items-center gap-4 p-3 bg-[#0D0D0D]/50 rounded-lg">
                  <img
                    src={gp.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(gp.name)}&background=C79F5E&color=000`}
                    alt={gp.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-semibold">{gp.name}</p>
                      <Badge className="bg-[#4D1A4A]/50 text-[#C79F5E] text-xs">
                        {gp.title}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-400">{gp.email}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold">{gp.affiliates_count}</p>
                    <p className="text-xs text-gray-400">afiliados</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#C79F5E] font-semibold">{formatCurrency(gp.godparent_balance)}</p>
                    <p className="text-xs text-gray-400">comissões</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Convites Pendentes */}
      <Card className="bg-[#1A1A1A] border-[#C79F5E]/20">
        <CardHeader>
          <CardTitle className="text-[#C79F5E]">Convites Criados</CardTitle>
        </CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <p className="text-gray-400 text-center py-4">Nenhum convite criado</p>
          ) : (
            <div className="space-y-2">
              {invites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-4 p-3 bg-[#0D0D0D]/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <code className="text-[#C79F5E] text-sm">{invite.token}</code>
                      <Badge variant={invite.used ? "default" : "outline"} className={invite.used ? "bg-green-500/20 text-green-400" : "border-yellow-500/50 text-yellow-400"}>
                        {invite.used ? 'Usado' : 'Pendente'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {invite.godparent_type} • {invite.godparent_status}
                      {invite.used_by_user && ` • Usado por: ${invite.used_by_user.name}`}
                    </p>
                  </div>
                  {!invite.used && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(`${BASE_URL}/?godparent_invite=${invite.token}`)}
                      className="border-[#C79F5E]/30"
                    >
                      Copiar Link
                    </Button>
                  )}
                  {!invite.used && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteInvite(invite.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Modal de Criar Convite */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal} modal={true}>
        <DialogContent className="bg-[#1A1A1A] border-[#C79F5E]/30" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-[#C79F5E]">🔥 Criar Convite de Padrinho/Madrinha</DialogTitle>
            <DialogDescription className="text-gray-400">
              Crie um link especial para convidar novos Padrinhos ou Madrinhas Hot
            </DialogDescription>
          </DialogHeader>
          
          {createdInvite ? (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-green-400 font-semibold mb-2">✅ Convite Criado!</p>
                <p className="text-white text-lg">{createdInvite.title}</p>
              </div>
              
              <div className="bg-[#0D0D0D] rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Link do convite:</p>
                <code className="text-[#C79F5E] text-sm break-all">
                  {createdInvite.invite_url}
                </code>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => copyLink(createdInvite.invite_url)}
                  className="flex-1 bg-[#C79F5E] text-black"
                >
                  Copiar Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="border-[#C79F5E]/30"
                >
                  Fechar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Tipo</label>
                <select 
                  value={newInvite.type} 
                  onChange={(e) => setNewInvite({...newInvite, type: e.target.value})}
                  className="w-full bg-[#0D0D0D] border border-[#C79F5E]/30 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#C79F5E]"
                >
                  <option value="padrinho">Padrinho Hot 🔥</option>
                  <option value="madrinha">Madrinha Hot 🔥</option>
                  <option value="casal_padrinho">Casal Padrinho Hot 🔥</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Status</label>
                <select 
                  value={newInvite.status} 
                  onChange={(e) => setNewInvite({...newInvite, status: e.target.value})}
                  className="w-full bg-[#0D0D0D] border border-[#C79F5E]/30 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#C79F5E]"
                >
                  <option value="solteiro">Solteiro</option>
                  <option value="solteira">Solteira</option>
                  <option value="casal">Casal</option>
                </select>
              </div>
              
              <div className="bg-[#4D1A4A]/20 border border-[#4D1A4A]/30 rounded-lg p-3">
                <p className="text-sm text-gray-300">
                  <span className="text-[#C79F5E]">💡 Info:</span> Padrinhos/Madrinhas recebem <strong>5%</strong> de comissão 
                  sobre toda receita (assinaturas e presentes) dos criadores que recrutarem.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={createInvite}
                  className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#8B6914] text-black"
                >
                  Criar Convite
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="border-[#C79F5E]/30"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGodparents;
